import { toast } from 'react-toastify';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import CustomToast from '../Components/Toast/toast';
import { ReactNode } from 'react';

const showToastError = (message: string, duration?: number) => {
  toast((props) => (
    <CustomToast {...props} type="error">
      {message as ReactNode}
    </CustomToast>
  ), {
    position: "top-right",
    autoClose: duration ?? 1500,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
  });
};

const notify = (message: string) => {
  showToastError(message)
};
const urlsToNotUse = ['assets'];
function createLoaderElement(): void {
  document.body.classList.add('loading-indicator');
}
function removeLoaderElement(): void {
  document.body.classList.remove('loading-indicator');
}
function isValidRequestForInterceptor(url: string): boolean {
  for (const address of urlsToNotUse) {
    if (new RegExp(address).test(url)) {
      return false;
    }
  }
  return true;
}

export const API = axios.create({
  baseURL: process.env.REACT_APP_API_KEY,
  responseType: 'json',
});

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string | undefined } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
    },
    unknown,
    unknown
  > =>
    async ({ url, method, data, params, ...extra }) => {
      axios.interceptors.response.use(
        (res) => {
          removeLoaderElement();
          if (res?.data?.error) {
            return Promise.reject(res?.data?.error);
          }
          return res;
        },
        async (err) => {
          removeLoaderElement();
          if (err.response.status === 403 || err.response.status === 404 || err.response.status === 401 || err.response.status === 402) {
            if (window.location.pathname !== '/login') {
              localStorage.removeItem('p_menu_token')
              window.location.replace('/')
            }
          }
          return Promise.reject(err);
        }
      );
      axios.interceptors.request.use(
        (config) => {
          if (isValidRequestForInterceptor(config.url!)) {
            createLoaderElement();
          }
          const token =
            localStorage.getItem('p_menu_token') &&
            localStorage.getItem('p_menu_token');
          if (config.url !== '/login' && !!config?.headers && token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          // Add Time-Zone only if request has _withTimeZone flag
          if ((extra as any)._withTimeZone && config.headers) {
            config.headers['Time-Zone'] =
              Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Yerevan';
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
      try {
        const result = await axios({ url: baseUrl + url, method, data, params, ...extra });
        return { data: result.data };
      } catch (axiosError) {
        let err = axiosError as any;
        if (err.response?.status !== 405 && err.response?.status !== 419 && err.response?.status !== 404 && err.response?.status !== 410) {
          notify(err?.response?.data?.message);
        }
        return {
          error: {
            error: err.response?.status,
            result: err.response?.data,
          },
        };
      }
    };

export default axiosBaseQuery;
